import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { auth } from './firebase';
import { toast } from 'react-toastify';
import { FaGoogle, FaFacebookF } from 'react-icons/fa';
import './Login.css';

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // robust redirect result handler
  useEffect(() => {
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (!result || !result.user) return;

        const token = await result.user.getIdToken();

        // providerId can be null; check fallback
        const providerId =
          result.providerId ||
          result?._tokenResponse?.providerId ||
          result?.user?.providerData?.[0]?.providerId;

        const provider =
          providerId === 'google.com'
            ? 'google'
            : providerId === 'facebook.com'
            ? 'facebook'
            : 'google'; // default to google if unknown

        await handleOAuthLogin(token, provider);
      } catch (err) {
        console.error('Error during OAuth redirect:', err);
        toast.error(`OAuth login error: ${err.message}`);
      }
    })();
  }, []);

  const handleOAuthLogin = async (providerToken, provider) => {
    try {
      const payload =
        provider === 'google'
          ? { googleToken: providerToken }
          : { facebookToken: providerToken };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API}/login`,
        payload,
        { withCredentials: true }
      );

      dispatch({ type: 'LOGIN_SUCCESS', payload: data });
      onLoginSuccess?.();
      navigate('/');
    } catch (error) {
      console.error(`${provider} login failed:`, error);
      dispatch({ type: 'LOGIN_FAILURE', payload: error?.message });
      toast.error(`${provider} login error: ${error?.response?.data?.message || error.message}`);
    }
  };

  const doGoogle = async (useRedirect = false) => {
    const provider = new GoogleAuthProvider();
    // Force the account chooser to appear:
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      if (useRedirect) {
        await signInWithRedirect(auth, provider);
      } else {
        const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
        const token = await result.user.getIdToken();
        await handleOAuthLogin(token, 'google');
      }
    } catch (err) {
      // If popup blocked, try redirect automatically
      if (!useRedirect) {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirErr) {
          console.error('Google redirect failed:', redirErr);
          toast.error(`Google login error: ${redirErr.message}`);
        }
      } else {
        console.error('Google login error:', err);
        toast.error(`Google login error: ${err.message}`);
      }
    }
  };

  const doFacebook = async (useRedirect = false) => {
    const provider = new FacebookAuthProvider();
    // Optional: force re-auth style dialog
    // provider.setCustomParameters({ auth_type: 'rerequest' });
    try {
      if (useRedirect) {
        await signInWithRedirect(auth, provider);
      } else {
        const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
        const token = await result.user.getIdToken();
        await handleOAuthLogin(token, 'facebook');
      }
    } catch (err) {
      if (!useRedirect) {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirErr) {
          console.error('Facebook redirect failed:', redirErr);
          toast.error(`Facebook login error: ${redirErr.message}`);
        }
      } else {
        console.error('Facebook login error:', err);
        toast.error(`Facebook login error: ${err.message}`);
      }
    }
  };

  const loginWithGoogle = () => doGoogle(false);
  const loginWithFacebook = () => doFacebook(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API}/login`,
        { email: email.toLowerCase().trim(), password },
        { withCredentials: true }
      );
      dispatch({ type: 'LOGIN_SUCCESS', payload: data });
      onLoginSuccess?.();
      navigate('/');
    } catch (error) {
      console.error('Login failed:', error);
      dispatch({ type: 'LOGIN_FAILURE', payload: error?.message });
      toast.error(`Login error: ${error?.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="login-container">
      <h2>Login to Your Account</h2>
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
            placeholder="Email"
            required
          />
        </div>
        <div className="input-group">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
          />
        </div>
        <div className="action-buttons">
          <button type="submit" className="login-btn">Login</button>
          <button type="button" className="social-login google" onClick={loginWithGoogle}>
            <FaGoogle /> Login with Google
          </button>
          <button type="button" className="social-login facebook" onClick={loginWithFacebook}>
            <FaFacebookF /> Login with Facebook
          </button>
        </div>
      </form>
    </div>
  );
};

export default Login;





// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { GoogleAuthProvider, signInWithRedirect, getRedirectResult, FacebookAuthProvider } from 'firebase/auth';
// import { auth } from './firebase';
// import { toast } from 'react-toastify';
// import { FaGoogle, FaFacebookF } from 'react-icons/fa';
// import './Login.css';

// const Login = ({ onLoginSuccess }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   // Check for redirect result on component mount
//   useEffect(() => {
//     const handleRedirectResult = async () => {
//       try {
//         const result = await getRedirectResult(auth);
//         if (result) {
//           const token = await result.user.getIdToken();
//           const provider = result.providerId.includes('google') ? 'google' : 'facebook';
//           handleOAuthLogin(token, provider);
//         }
//       } catch (err) {
//         console.error('Error during OAuth redirect:', err);
//         toast.error(`OAuth login error: ${err.message}`);
//       }
//     };
//     handleRedirectResult();
//   }, []);

//   const handleOAuthLogin = async (providerToken, provider) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { 
//           ...(provider === 'google' ? { googleToken: providerToken } : { facebookToken: providerToken })
//         },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       navigate('/');
//     } catch (error) {
//       console.error(`${provider} login failed:`, error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//       toast.error(`${provider} login error: ${error.message}`);
//     }
//   };

//   const loginWithGoogle = async () => {
//     const provider = new GoogleAuthProvider();
//     try {
//       await signInWithRedirect(auth, provider);
//       // User will be redirected, result handled in useEffect
//     } catch (err) {
//       console.error('Error during Google login:', err);
//       toast.error(`Google login error: ${err.message}`);
//     }
//   };

//   const loginWithFacebook = async () => {
//     const provider = new FacebookAuthProvider();
//     try {
//       await signInWithRedirect(auth, provider);
//       // User will be redirected, result handled in useEffect
//     } catch (err) {
//       console.error('Error during Facebook login:', err);
//       toast.error(`Facebook login error: ${err.message}`);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { email, password },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       navigate('/');
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//       toast.error(`Login error: ${error.message}`);
//     }
//   };

//   return (
//     <div className="login-container">
//       <h2>Login to Your Account</h2>
//       <form className="login-form" onSubmit={handleSubmit}>
//         <div className="input-group">
//           <input
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
//             placeholder="Email"
//             required
//           />
//         </div>
//         <div className="input-group">
//           <input
//             type="password"
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             placeholder="Password"
//             required
//           />
//         </div>
//         <div className="action-buttons">
//           <button type="submit" className="login-btn">Login</button>
//           <button type="button" className="social-login google" onClick={loginWithGoogle}>
//             <FaGoogle /> Login with Google
//           </button>
//           <button type="button" className="social-login facebook" onClick={loginWithFacebook}>
//             <FaFacebookF /> Login with Facebook
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default Login;

// // import React, { useState } from 'react';
// // import axios from 'axios';
// // import { useDispatch } from 'react-redux';
// // import { useNavigate, Link } from 'react-router-dom';
// // import { GoogleAuthProvider, signInWithPopup, FacebookAuthProvider } from 'firebase/auth';
// // import { auth } from './firebase'; // Ensure you import your Firebase auth configuration
// // import { toast } from 'react-toastify';
// // import { FaGoogle, FaFacebookF } from 'react-icons/fa';
// // import './Login.css'; // Assuming you will create a separate CSS file for login styles

// // const Login = ({ onLoginSuccess }) => {
// //   const [email, setEmail] = useState('');
// //   const [password, setPassword] = useState('');
// //   const dispatch = useDispatch();
// //   const navigate = useNavigate(); // Changed from useHistory to useNavigate

// //   const handleOAuthLogin = async (providerToken, provider) => {
// //     try {
// //       const response = await axios.post(
// //         `${process.env.REACT_APP_API}/login`, 
// //         { 
// //           ...(provider === 'google' ? { googleToken: providerToken } : { facebookToken: providerToken })
// //         },
// //         { withCredentials: true }
// //       );
// //       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
// //       onLoginSuccess();
// //       navigate('/'); // Changed from history.push to navigate
// //     } catch (error) {
// //       console.error(`${provider} login failed:`, error);
// //       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
// //       toast.error(`${provider} login error: ${error.message}`);
// //     }
// //   };

// //   const loginWithGoogle = async () => {
// //     const provider = new GoogleAuthProvider();
// //     try {
// //       const result = await signInWithPopup(auth, provider);
// //       const googleToken = await result.user.getIdToken();
// //       handleOAuthLogin(googleToken, 'google');
// //     } catch (err) {
// //       console.error('Error during Google login:', err);
// //       toast.error(`Google login error: ${err.message}`);
// //     }
// //   };

// //   const loginWithFacebook = async () => {
// //     const provider = new FacebookAuthProvider();
// //     try {
// //       const result = await signInWithPopup(auth, provider);
// //       const facebookToken = await result.user.getIdToken();
// //       handleOAuthLogin(facebookToken, 'facebook');
// //     } catch (err) {
// //       console.error('Error during Facebook login:', err);
// //       toast.error(`Facebook login error: ${err.message}`);
// //     }
// //   };

// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
// //     try {
// //       const response = await axios.post(
// //         `${process.env.REACT_APP_API}/login`, 
// //         { email, password },
// //         { withCredentials: true }
// //       );
// //       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
// //       onLoginSuccess();
// //       navigate('/'); // Changed from history.push to navigate
// //     } catch (error) {
// //       console.error('Login failed:', error);
// //       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
// //       toast.error(`Login error: ${error.message}`);
// //     }
// //   };

// //   return (
// //     <div className="login-container">
// //       <h2>Login to Your Account</h2>
// //       <form className="login-form" onSubmit={handleSubmit}>
// //         <div className="input-group">
// //           <input
// //             type="email"
// //             value={email}
// //             onChange={(e) => setEmail(e.target.value)}
// //             placeholder="Email"
// //             required
// //           />
// //         </div>
// //         <div className="input-group">
// //           <input
// //             type="password"
// //             value={password}
// //             onChange={(e) => setPassword(e.target.value)}
// //             placeholder="Password"
// //             required
// //           />
// //         </div>
// //         <div className="action-buttons">
// //           <button type="submit" className="login-btn">Login</button>
// //           <button type="button" className="social-login google" onClick={loginWithGoogle}>
// //             <FaGoogle /> Login with Google
// //           </button>
// //           <button type="button" className="social-login facebook" onClick={loginWithFacebook}>
// //             <FaFacebookF /> Login with Facebook
// //           </button>
// //         </div>
// //         {/* <p className="forgot-password">
// //           Forgot your password? <Link to="/password/help">Reset it here</Link>
// //         </p> */}
// //       </form>
// //     </div>
// //   );
// // };

// // export default Login;





// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useNavigate, Link } from 'react-router-dom';
// import { GoogleAuthProvider, signInWithPopup, FacebookAuthProvider } from 'firebase/auth';
// import { auth } from './firebase'; // Ensure you import your Firebase auth configuration
// import { toast } from 'react-toastify';
// import { FaGoogle, FaFacebookF } from 'react-icons/fa';
// import './Login.css'; // Assuming you will create a separate CSS file for login styles

// const Login = ({ onLoginSuccess }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const navigate = useNavigate(); // Changed from useHistory to useNavigate

//   const handleOAuthLogin = async (providerToken, provider) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { 
//           ...(provider === 'google' ? { googleToken: providerToken } : { facebookToken: providerToken })
//         },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       navigate('/'); // Changed from history.push to navigate
//     } catch (error) {
//       console.error(`${provider} login failed:`, error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//       toast.error(`${provider} login error: ${error.message}`);
//     }
//   };

//   const loginWithGoogle = async () => {
//     const provider = new GoogleAuthProvider();
//     try {
//       const result = await signInWithPopup(auth, provider);
//       const googleToken = await result.user.getIdToken();
//       handleOAuthLogin(googleToken, 'google');
//     } catch (err) {
//       console.error('Error during Google login:', err);
//       toast.error(`Google login error: ${err.message}`);
//     }
//   };

//   const loginWithFacebook = async () => {
//     const provider = new FacebookAuthProvider();
//     try {
//       const result = await signInWithPopup(auth, provider);
//       const facebookToken = await result.user.getIdToken();
//       handleOAuthLogin(facebookToken, 'facebook');
//     } catch (err) {
//       console.error('Error during Facebook login:', err);
//       toast.error(`Facebook login error: ${err.message}`);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { email, password },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       navigate('/'); // Changed from history.push to navigate
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//       toast.error(`Login error: ${error.message}`);
//     }
//   };

//   return (
//     <div className="login-container">
//       <h2>Login to Your Account</h2>
//       <form className="login-form" onSubmit={handleSubmit}>
//         <div className="input-group">
//           <input
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             placeholder="Email"
//             required
//           />
//         </div>
//         <div className="input-group">
//           <input
//             type="password"
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             placeholder="Password"
//             required
//           />
//         </div>
//         <div className="action-buttons">
//           <button type="submit" className="login-btn">Login</button>
//           <button type="button" className="social-login google" onClick={loginWithGoogle}>
//             <FaGoogle /> Login with Google
//           </button>
//           <button type="button" className="social-login facebook" onClick={loginWithFacebook}>
//             <FaFacebookF /> Login with Facebook
//           </button>
//         </div>
//         <p className="forgot-password">
//           Forgot your password? <Link to="/password/help">Reset it here</Link>
//         </p>
//       </form>
//     </div>
//   );
// };

// export default Login;





// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useHistory, Link } from 'react-router-dom';
// import { GoogleAuthProvider, signInWithPopup, FacebookAuthProvider } from 'firebase/auth';
// import { auth } from './firebase'; // Ensure you import your Firebase auth configuration
// import { toast } from 'react-toastify';
// import { FaGoogle, FaFacebookF } from 'react-icons/fa';
// import './Login.css'; // Assuming you will create a separate CSS file for login styles

// const Login = ({ onLoginSuccess }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const history = useHistory();

//   const handleOAuthLogin = async (providerToken, provider) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { 
//           ...(provider === 'google' ? { googleToken: providerToken } : { facebookToken: providerToken })
//         },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       history.push('/');
//     } catch (error) {
//       console.error(`${provider} login failed:`, error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//       toast.error(`${provider} login error: ${error.message}`);
//     }
//   };

//   const loginWithGoogle = async () => {
//     const provider = new GoogleAuthProvider();
//     try {
//       const result = await signInWithPopup(auth, provider);
//       const googleToken = await result.user.getIdToken();
//       handleOAuthLogin(googleToken, 'google');
//     } catch (err) {
//       console.error('Error during Google login:', err);
//       toast.error(`Google login error: ${err.message}`);
//     }
//   };

//   const loginWithFacebook = async () => {
//     const provider = new FacebookAuthProvider();
//     try {
//       const result = await signInWithPopup(auth, provider);
//       const facebookToken = await result.user.getIdToken();
//       handleOAuthLogin(facebookToken, 'facebook');
//     } catch (err) {
//       console.error('Error during Facebook login:', err);
//       toast.error(`Facebook login error: ${err.message}`);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { email, password },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       history.push('/');
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//     }
//   };

//   return (
//     <div className="login-container">
//       <h2>Login to Your Account</h2>
//       <form className="login-form" onSubmit={handleSubmit}>
//         <div className="input-group">
//           <input
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             placeholder="Email"
//             required
//           />
//         </div>
//         <div className="input-group">
//           <input
//             type="password"
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             placeholder="Password"
//             required
//           />
//         </div>
//         <div className="action-buttons">
//           <button type="submit" className="login-btn">Login</button>
//           <button type="button" className="social-login google" onClick={loginWithGoogle}>
//   <FaGoogle /> Login with Google
// </button>
// <button type="button" className="social-login facebook" onClick={loginWithFacebook}>
//   <FaFacebookF /> Login with Facebook
// </button>
//         </div>
//         <p className="forgot-password">
//           Forgot your password? <Link to="/password/help">Reset it here</Link>
//         </p>
//       </form>
//     </div>
//   );
// };

// export default Login;



// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useHistory, Link } from 'react-router-dom';
// import { GoogleAuthProvider, signInWithPopup, FacebookAuthProvider } from 'firebase/auth';
// import { auth } from './firebase'; // Ensure you import your Firebase auth configuration
// import { toast } from 'react-toastify';


// const Login = ({ onLoginSuccess }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const history = useHistory();

//   const handleOAuthLogin = async (providerToken, provider) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { 
//           ...(provider === 'google' ? { googleToken: providerToken } : { facebookToken: providerToken })
//         },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       history.push('/');
//     } catch (error) {
//       console.error(`${provider} login failed:`, error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//       toast.error(`${provider} login error: ${error.message}`);
//     }
//   };

//   const loginWithGoogle = async () => {
//     const provider = new GoogleAuthProvider();
//     try {
//       const result = await signInWithPopup(auth, provider);
//       const googleToken = await result.user.getIdToken();
//       handleOAuthLogin(googleToken, 'google');
//     } catch (err) {
//       console.error('Error during Google login:', err);
//       toast.error(`Google login error: ${err.message}`);
//     }
//   };

//   const loginWithFacebook = async () => {
//     const provider = new FacebookAuthProvider();
//     try {
//       const result = await signInWithPopup(auth, provider);
//       const facebookToken = await result.user.getIdToken();
//       handleOAuthLogin(facebookToken, 'facebook');
//     } catch (err) {
//       console.error('Error during Facebook login:', err);
//       toast.error(`Facebook login error: ${err.message}`);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { email, password },
//         { withCredentials: true }
//       );
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       onLoginSuccess();
//       history.push('/');
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//     }
//   };

//   return (
//     <div>
//       <h2>Login</h2>
//       <form onSubmit={handleSubmit}>
//         <input
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           placeholder="Email"
//           required
//         />
//         <input
//           type="password"
//           value={password}
//           onChange={(e) => setPassword(e.target.value)}
//           placeholder="Password"
//           required
//         />
//         <button type="submit">Login</button>
//         <button type="button" onClick={loginWithGoogle}>Login with Google</button>
//         <button type="button" onClick={loginWithFacebook}>Login with Facebook</button>
//         <p>
//           Forgot your password? <Link to="/password/help">Reset it here</Link>
//         </p>
//       </form>
//     </div>
//   );
// };

// export default Login;








// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useHistory, Link } from 'react-router-dom';


// const Login = ({ onLoginSuccess }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const history = useHistory();

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       // Make the login request with email and password
//       const response = await axios.post(
//         `${process.env.REACT_APP_API}/login`, 
//         { email, password },
//         { withCredentials: true } // This ensures cookies are sent and received
//       );

//       // Dispatch login success and store user info (not token) in state
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });

//       // Call onLoginSuccess to update the App's state if needed
//       onLoginSuccess(); 

//       // Navigate to the homepage after state is updated
//       history.push('/');
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//     }
//   };

//   return (
//     <div>
//       <h2>Login</h2>
//       <form onSubmit={handleSubmit}>
//         <input
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           placeholder="Email"
//           required
//         />
//         <input
//           type="password"
//           value={password}
//           onChange={(e) => setPassword(e.target.value)}
//           placeholder="Password"
//           required
//         />
//         <button type="submit">Login</button>
//         <p>
//           Forgot your password? <Link to="/password/help">Reset it here</Link>
//         </p>
//       </form>
//     </div>
//   );
// };

// export default Login;





// gtg before HTTPonly cookie and same site


// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useHistory } from 'react-router-dom';

// const Login = ({ onLoginSuccess }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const history = useHistory();

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(`${process.env.REACT_APP_API}/login`, { email, password });

//       // Save token in localStorage and dispatch login success
//       window.localStorage.setItem('auth', JSON.stringify(response.data));
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });

//       // Call onLoginSuccess to update the App's state
//       onLoginSuccess(); 

//       // Navigate to the homepage after state is updated
//       history.push('/');
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//     }
//   };

//   return (
//     <div>
//       <h2>Login</h2>
//       <form onSubmit={handleSubmit}>
//         <input
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           placeholder="Email"
//           required
//         />
//         <input
//           type="password"
//           value={password}
//           onChange={(e) => setPassword(e.target.value)}
//           placeholder="Password"
//           required
//         />
//         <button type="submit">Login</button>
//       </form>
//     </div>
//   );
// };

// export default Login;












// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { useHistory } from 'react-router-dom';  // Import useHistory from react-router-dom

// const Login = () => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();
//   const history = useHistory();  // Initialize useHistory

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(`${process.env.REACT_APP_API}/login`, { email, password });

//       console.log('Login response:', response.data);
//       window.localStorage.setItem('auth', JSON.stringify(response.data));
//       console.log('Stored auth token:', JSON.parse(window.localStorage.getItem('auth')));

//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
//       history.push('/');  // Use history.push to navigate to the homepage
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//     }
//   };

//   return (
//     <div>
//       <h2>Login</h2>
//       <form onSubmit={handleSubmit}>
//         <input
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           placeholder="Email"
//           required
//         />
//         <input
//           type="password"
//           value={password}
//           onChange={(e) => setPassword(e.target.value)}
//           placeholder="Password"
//           required
//         />
//         <button type="submit">Login</button>
//       </form>
//     </div>
//   );
// };

// export default Login;











// // src/Login.js
// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';

// const Login = ({ history }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(`${process.env.REACT_APP_API}/login`, { email, password });

//       // Log the response to ensure the JWT token is received
//       console.log('Login response:', response.data);

//       // Save the JWT token to localStorage
//       window.localStorage.setItem('auth', JSON.stringify(response.data));

//       // Log the stored token to confirm it's correctly saved
//       console.log('Stored auth token:', JSON.parse(window.localStorage.getItem('auth')));

//       // Dispatch login success action
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });

//       // Redirect to the homepage
//       history.push('/');
//     } catch (error) {
//       console.error('Login failed:', error);

//       // Dispatch login failure action
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
//     }
//   };

//   return (
//     <div>
//       <h2>Login</h2>
//       <form onSubmit={handleSubmit}>
//         <input
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           placeholder="Email"
//           required
//         />
//         <input
//           type="password"
//           value={password}
//           onChange={(e) => setPassword(e.target.value)}
//           placeholder="Password"
//           required
//         />
//         <button type="submit">Login</button>
//       </form>
//     </div>
//   );
// };

// export default Login;




// // src/Login.js
// import React, { useState } from 'react';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';

// const Login = ({ history }) => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const dispatch = useDispatch();

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await axios.post(`${process.env.REACT_APP_API}/login`, { email, password });
//       window.localStorage.setItem('auth', JSON.stringify(response.data)); // Save entire response data to localStorage
//       dispatch({ type: 'LOGIN_SUCCESS', payload: response.data }); // Dispatch login success action
//       history.push('/');
//     } catch (error) {
//       console.error('Login failed:', error);
//       dispatch({ type: 'LOGIN_FAILURE', payload: error.message }); // Dispatch login failure action
//     }
//   };

//   return (
//     <div>
//       <h2>Login</h2>
//       <form onSubmit={handleSubmit}>
//         <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />
//         <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required />
//         <button type="submit">Login</button>
//       </form>
//     </div>
//   );
// };

// export default Login;
